// 편집기 보조: 입력 마스크, 달력 계산 (DOM 없음)
/** 마스크 적용: # 숫자, A 영문, * 아무 글자, 그 외는 고정 문자 (입력 중이면 다음 슬롯 앞 고정 문자까지만) */
export function applyMask(raw, mask) {
    const src = [...unmask(raw, mask)];
    let out = '', i = 0;
    for (const m of mask) {
        if (i >= src.length)
            break;
        if (m === '#' || m === 'A' || m === '*')
            out += src[i++];
        else
            out += m;
    }
    return out;
}
const slotOk = (m, ch) => (m === '#' ? /\d/.test(ch) : m === 'A' ? /[a-z]/i.test(ch) : true);
/** 마스크의 고정 문자를 빼고 슬롯에 맞는 글자만 (저장 값) */
export function unmask(text, mask) {
    const slots = [...mask].filter(m => m === '#' || m === 'A' || m === '*');
    const lits = new Set([...mask].filter(m => m !== '#' && m !== 'A' && m !== '*'));
    let out = '';
    for (const ch of String(text ?? '')) {
        if (out.length >= slots.length)
            break;
        if (lits.has(ch) && slots[out.length] !== '*')
            continue;
        if (slotOk(slots[out.length], ch))
            out += ch;
    }
    return out;
}
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const fromIso = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
export function todayIso() { return iso(new Date()); }
export function addDays(s, n) { const d = fromIso(s); d.setDate(d.getDate() + n); return iso(d); }
/** 달 이동: 말일 넘침은 그 달 말일로 (1/31 + 1달 = 2/28) */
export function addMonths(s, n) {
    const d = fromIso(s), day = d.getDate();
    const t = new Date(d.getFullYear(), d.getMonth() + n, 1);
    const last = new Date(t.getFullYear(), t.getMonth() + 1, 0).getDate();
    t.setDate(Math.min(day, last));
    return iso(t);
}
/** 달력 6주(42일): 그 달 1일이 든 주의 일요일부터 */
export function calendarDays(s) {
    const d = fromIso(s), first = new Date(d.getFullYear(), d.getMonth(), 1);
    first.setDate(1 - first.getDay());
    return Array.from({ length: 42 }, (_, i) => { const x = new Date(first); x.setDate(first.getDate() + i); return iso(x); });
}
/** 시간 입력 해석: 0930·930·9:5·9 → "09:30"·"09:30"·"09:05"·"09:00". 잘못되면 null */
export function parseTime(text) {
    const t = String(text ?? '').trim();
    let h, m;
    const a = t.match(/^(\d{1,2})\s*[:시.]\s*(\d{1,2})\s*분?$/), b = t.match(/^(\d{1,4})$/);
    if (a) {
        h = +a[1];
        m = +a[2];
    }
    else if (b) {
        const d = b[1];
        if (d.length <= 2) {
            h = +d;
            m = 0;
        }
        else {
            h = +d.slice(0, d.length - 2);
            m = +d.slice(-2);
        }
    }
    else
        return null;
    if (h > 23 || m > 59)
        return null;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}
/** "HH:mm" 에 분 더하기 (24시간 순환). 해석 안 되면 00:00 기준 */
export function stepTime(hhmm, minutes) {
    const p = parseTime(hhmm) ?? '00:00';
    const t = ((+p.slice(0, 2) * 60 + +p.slice(3) + minutes) % 1440 + 1440) % 1440;
    return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
}
/** 채우기 핸들 값: 숫자·날짜가 2개 이상이고 간격이 같으면 계열 연장, 아니면 원본을 반복 복사 */
export function fillSeries(src, n) {
    const out = [];
    if (!src.length || n <= 0)
        return out;
    const L = src.length, same = (d) => d.every(x => Math.abs(x - d[0]) < 1e-9);
    if (L >= 2 && src.every(v => typeof v === 'number' && isFinite(v))) {
        const s = src, d = s.slice(1).map((v, i) => v - s[i]);
        if (same(d)) {
            for (let k = 1; k <= n; k++)
                out.push(Math.round((s[L - 1] + d[0] * k) * 1e9) / 1e9);
            return out;
        }
    }
    if (L >= 2 && src.every(v => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v))) {
        const t = src.map(v => Date.UTC(+v.slice(0, 4), +v.slice(5, 7) - 1, +v.slice(8, 10)) / 864e5), d = t.slice(1).map((v, i) => v - t[i]);
        if (same(d)) {
            for (let k = 1; k <= n; k++)
                out.push(addDays(src[L - 1], d[0] * k));
            return out;
        }
    }
    // 문자열 + 숫자 꼬리 ("검체1" → "검체2", "A-009" → "A-010"): 앞부분이 같으면 꼬리 숫자를 이어 감 (1개면 +1)
    const tails = src.map(v => (typeof v === 'string' ? /^(.*?)(\d+)$/.exec(v) : null));
    if (tails.every(m => m && m[1] === tails[0][1]) && !(L === 1 && /^\d+$/.test(src[0]))) {
        const nums = tails.map(m => +m[2]), d = L === 1 ? [1] : nums.slice(1).map((v, i) => v - nums[i]), w = tails[L - 1][2].length, pad = tails[L - 1][2].startsWith('0');
        if (same(d)) {
            for (let k = 1; k <= n; k++) {
                const x = nums[L - 1] + d[0] * k;
                out.push(x < 0 ? src[(k - 1) % L] : tails[0][1] + (pad ? String(x).padStart(w, '0') : String(x)));
            }
            return out;
        }
    }
    for (let k = 0; k < n; k++)
        out.push(src[k % L]);
    return out;
}
const CLS = {
    d: c => c >= '0' && c <= '9', w: c => /\w/.test(c), s: c => /\s/.test(c),
    D: c => !(c >= '0' && c <= '9'), W: c => !/\w/.test(c), S: c => !/\s/.test(c),
};
/** 정규식 일부 문법 → AST (글자·.·\d\w\s·[..]·(?:)·()·|·*+?{n,m}·^$). 역참조·전후방 탐색 등은 throw */
function parseRe(src, icase) {
    let i = 0;
    const lc = (c) => (icase ? c.toLowerCase() : c);
    const lit = (ch) => { const x = lc(ch); return c => lc(c) === x; };
    const esc = (inClass) => {
        const c = src[i++];
        if (c == null)
            throw new Error('끝');
        if (CLS[c])
            return CLS[c];
        if (c === 'u' && /^[0-9a-fA-F]{4}$/.test(src.slice(i, i + 4))) {
            const ch = String.fromCharCode(parseInt(src.slice(i, i + 4), 16));
            i += 4;
            return ch;
        }
        if (c === 't')
            return '\t';
        if (c === 'n')
            return '\n';
        if (/[0-9bBkpP]/.test(c) && !(inClass && c === 'b'))
            throw new Error('지원 안 함 \\' + c);
        return c;
    };
    const klass = () => {
        const neg = src[i] === '^';
        if (neg)
            i++;
        const parts = [];
        let first = true;
        while (i < src.length && (src[i] !== ']' || first)) {
            first = false;
            let a = src[i++];
            if (a === '\\')
                a = esc(true);
            if (typeof a === 'string' && src[i] === '-' && src[i + 1] && src[i + 1] !== ']') {
                i++;
                let b = src[i++];
                if (b === '\\')
                    b = esc(true);
                if (typeof b !== 'string')
                    throw new Error('범위');
                const lo = a, hi = b;
                parts.push(icase ? c => { const l = c.toLowerCase(), u = c.toUpperCase(); return (l >= lo && l <= hi) || (u >= lo && u <= hi); } : c => c >= lo && c <= hi);
            }
            else
                parts.push(typeof a === 'string' ? lit(a) : a);
        }
        if (src[i] !== ']')
            throw new Error('] 없음');
        i++;
        return c => parts.some(p => p(c)) !== neg;
    };
    const atom = () => {
        const c = src[i];
        if (c === '(') {
            i++;
            if (src[i] === '?') {
                if (src[i + 1] !== ':')
                    throw new Error('전후방 탐색');
                i += 2;
            }
            const a = alt();
            if (src[i] !== ')')
                throw new Error(') 없음');
            i++;
            return a;
        }
        if (c === '[') {
            i++;
            return { k: 'ch', f: klass() };
        }
        if (c === '.') {
            i++;
            return { k: 'ch', f: x => x !== '\n' };
        }
        if (c === '^' || c === '$') {
            i++;
            return { k: 'seq', a: [] };
        }
        if (c === '\\') {
            i++;
            const t = esc(false);
            return { k: 'ch', f: typeof t === 'string' ? lit(t) : t };
        }
        if (c === '*' || c === '+' || c === '?' || c === '{' && /^\{\d/.test(src.slice(i)))
            throw new Error('반복 대상 없음');
        i++;
        return { k: 'ch', f: lit(c) };
    };
    const seq = () => {
        const out = [];
        while (i < src.length && src[i] !== '|' && src[i] !== ')') {
            let a = atom();
            for (;;) {
                const q = src[i];
                let min = -1, max = -1;
                if (q === '*') {
                    min = 0;
                    max = Infinity;
                    i++;
                }
                else if (q === '+') {
                    min = 1;
                    max = Infinity;
                    i++;
                }
                else if (q === '?') {
                    min = 0;
                    max = 1;
                    i++;
                }
                else if (q === '{') {
                    const m = /^\{(\d+)(,(\d*))?\}/.exec(src.slice(i));
                    if (!m)
                        break;
                    min = +m[1];
                    max = m[2] ? (m[3] ? +m[3] : Infinity) : min;
                    i += m[0].length;
                }
                else
                    break;
                if (src[i] === '?')
                    i++; // 게으른 반복은 같게 봄 (일치 여부만)
                a = { k: 'rep', a, min, max };
            }
            out.push(a);
        }
        return out.length === 1 ? out[0] : { k: 'seq', a: out };
    };
    const alt = () => { const a = [seq()]; while (src[i] === '|') {
        i++;
        a.push(seq());
    } return a.length === 1 ? a[0] : { k: 'alt', a }; };
    const r = alt();
    if (i < src.length)
        throw new Error('해석 못 함');
    return r;
}
/** AST → Thompson NFA (반복은 복제, 상태 4000개 넘으면 throw) */
function buildNfa(ast) {
    const eps = [], test = [], next = [];
    const st = () => { if (eps.length > 4000)
        throw new Error('너무 큼'); eps.push([]); test.push(null); next.push(-1); return eps.length - 1; };
    const comp = (a) => {
        if (a.k === 'ch') {
            const s = st(), e = st();
            test[s] = a.f;
            next[s] = e;
            return [s, e];
        }
        if (a.k === 'seq') {
            const s = st();
            let cur = s;
            for (const x of a.a) {
                const [xs, xe] = comp(x);
                eps[cur].push(xs);
                cur = xe;
            }
            return [s, cur];
        }
        if (a.k === 'alt') {
            const s = st(), e = st();
            for (const x of a.a) {
                const [xs, xe] = comp(x);
                eps[s].push(xs);
                eps[xe].push(e);
            }
            return [s, e];
        }
        const s = st();
        let cur = s;
        for (let k = 0; k < a.min; k++) {
            const [xs, xe] = comp(a.a);
            eps[cur].push(xs);
            cur = xe;
        }
        const e = st();
        if (a.max === Infinity) {
            const [xs, xe] = comp(a.a);
            eps[cur].push(xs, e);
            eps[xe].push(xs, e);
        }
        else {
            for (let k = a.min; k < a.max; k++) {
                const [xs, xe] = comp(a.a);
                eps[cur].push(xs, e);
                cur = xe;
            }
            eps[cur].push(e);
        }
        return [s, e];
    };
    const [start, accept] = comp(ast);
    return { eps, test, next, start, accept };
}
function nfaRun(n, text) {
    const close = (set) => { const stack = [...set]; while (stack.length) {
        const s = stack.pop();
        for (const t of n.eps[s])
            if (!set.has(t)) {
                set.add(t);
                stack.push(t);
            }
    } return set; };
    let cur = close(new Set([n.start]));
    for (const ch of text) {
        const nx = new Set();
        for (const s of cur) {
            const f = n.test[s];
            if (f && f(ch))
                nx.add(n.next[s]);
        }
        if (!nx.size)
            return nx;
        cur = close(nx);
    }
    return cur;
}
const reCache = new Map();
/** 컬럼 mask → 정규식 마스크 (표준 마스크 문자열이면 null). 결과는 캐시 */
export function regexMaskOf(m) {
    if (m == null || typeof m === 'string')
        return null;
    const src = m instanceof RegExp ? m.source : m.kind === 'regex' && typeof m.pattern === 'string' ? m.pattern : null;
    if (src == null)
        return null;
    const flags = (m instanceof RegExp ? m.flags : m.flags ?? '').replace(/[gy]/g, '');
    const ph = m instanceof RegExp ? undefined : m.placeholder, msg = m instanceof RegExp ? undefined : m.message;
    const key = src + '\u0001' + flags + '\u0001' + (ph ?? '') + '\u0001' + (msg ?? '');
    let r = reCache.get(key);
    if (!r) {
        let nfa = null;
        try {
            nfa = buildNfa(parseRe(src, flags.includes('i')));
        }
        catch {
            nfa = null;
        }
        r = { full: new RegExp('^(?:' + src + ')$', flags), nfa, placeholder: ph, message: msg, source: src };
        if (reCache.size > 200)
            reCache.clear();
        reCache.set(key, r);
    }
    return r;
}
/** 입력 중 검사: 더 쳐서 맞출 수 있는 앞부분이면 true (빈 글자는 true). 해석 못 한 정규식은 항상 true */
export function regexPrefixOk(m, text) {
    if (!text || !m.nfa)
        return true;
    return nfaRun(m.nfa, text).size > 0;
}
/** 확정 검사: 전체 일치 (빈 글자는 true — 필수는 required 로) */
export function regexFullOk(m, text) { return !text || m.full.test(text); }
/** 입력 글자 걸러내기: 앞에서부터 맞는 앞부분까지만 남김 (붙여넣기·한 번에 여러 글자) */
export function regexClip(m, text) {
    if (regexPrefixOk(m, text))
        return text;
    let ok = '';
    for (const ch of text) {
        if (regexPrefixOk(m, ok + ch))
            ok += ch;
    }
    return ok;
}
// ───────────── 날짜·시간 편집기 (QuantumGrid DateEdit Kind/MinDate/MaxDate/WeekNumbers) ─────────────
const p2 = (n) => String(n).padStart(2, '0');
/** 저장 값(YYYY-MM-DD·YYYYMMDD·…THH:mm·YYYYMMDDHHmm) → 비교용 ISO "YYYY-MM-DD" 또는 "YYYY-MM-DDTHH:mm" */
export function isoOf(v) {
    if (v == null || v === '')
        return null;
    const s = String(v).trim();
    let m = /^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})?$/.exec(s);
    if (m)
        return `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}`;
    m = /^(\d{4})(\d{2})(\d{2})$/.exec(s);
    if (m)
        return `${m[1]}-${m[2]}-${m[3]}`;
    m = /^(\d{4}-\d{2}-\d{2})(?:[T ](\d{2}:\d{2}))?/.exec(s);
    return m ? (m[2] ? `${m[1]}T${m[2]}` : m[1]) : null;
}
/** 날짜·시간 입력 해석: "20261010 0930", "2026-10-10 9:30", "1010 930"(올해), "오늘 14:00", "지금" → "YYYY-MM-DDTHH:mm" (out 'YYYYMMDD' 면 YYYYMMDDHHmm). 시간이 없으면 00:00 */
export function parseDateTime(text, out = 'YYYY-MM-DD', now = new Date()) {
    const t = String(text ?? '').trim();
    if (!t)
        return null;
    let date, time = '00:00';
    if (/^(now|지금)$/i.test(t)) {
        date = `${now.getFullYear()}-${p2(now.getMonth() + 1)}-${p2(now.getDate())}`;
        time = `${p2(now.getHours())}:${p2(now.getMinutes())}`;
    }
    else if (/^\d{12}$/.test(t)) {
        date = `${t.slice(0, 4)}-${t.slice(4, 6)}-${t.slice(6, 8)}`;
        time = parseTime(t.slice(8));
    }
    else {
        const m = /^(.+?)(?:[ T]+(\S+(?:\s*분)?))?$/.exec(t);
        const ds = /^(t|today|오늘)$/i.test(m[1]) ? `${now.getFullYear()}-${p2(now.getMonth() + 1)}-${p2(now.getDate())}` : m[1];
        date = parseIsoDate(ds, now);
        if (m[2])
            time = parseTime(m[2].replace(/시$/, ''));
    }
    if (!date || !time)
        return null;
    return out === 'YYYYMMDD' ? date.replace(/-/g, '') + time.replace(':', '') : `${date}T${time}`;
}
/** parseDate 와 같은 규칙의 날짜 해석 (format.ts 를 끌어오지 않으려고 따로 — 8·6·4자리, 구분자 3부분) */
function parseIsoDate(t, now) {
    let y, m, d;
    const digits = t.replace(/[^\d]/g, ''), parts = t.split(/[^\d]+/).filter(Boolean);
    if (parts.length === 3) {
        y = +parts[0];
        m = +parts[1];
        d = +parts[2];
        if (y < 100)
            y += 2000;
    }
    else if (digits.length === 8) {
        y = +digits.slice(0, 4);
        m = +digits.slice(4, 6);
        d = +digits.slice(6, 8);
    }
    else if (digits.length === 6) {
        y = 2000 + +digits.slice(0, 2);
        m = +digits.slice(2, 4);
        d = +digits.slice(4, 6);
    }
    else if (digits.length === 4) {
        y = now.getFullYear();
        m = +digits.slice(0, 2);
        d = +digits.slice(2, 4);
    }
    else
        return null;
    const dt = new Date(y, m - 1, d);
    if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d)
        return null;
    return `${y}-${p2(m)}-${p2(d)}`;
}
/** 범위 검사 (순수): 날짜만이면 날짜 부분끼리, 날짜·시간이면 분 단위. 결과: null(통과) | 'min' | 'max' */
export function dateRangeError(v, min, max) {
    const x = isoOf(v);
    if (!x)
        return null;
    const cut = (a, b) => (a.length === 10 || b.length === 10 ? [a.slice(0, 10), b.slice(0, 10)] : [a, b]);
    const lo = isoOf(min), hi = isoOf(max);
    if (lo) {
        const [a, b] = cut(x, lo);
        if (a < b)
            return 'min';
    }
    if (hi) {
        const [a, b] = cut(x, hi);
        if (a > b)
            return 'max';
    }
    return null;
}
/** ISO 8601 주 번호 (월요일 시작, 1월 4일이 든 주가 1주) */
export function isoWeek(iso) {
    const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
    const t = new Date(Date.UTC(y, m - 1, d)), day = t.getUTCDay() || 7;
    t.setUTCDate(t.getUTCDate() + 4 - day);
    const y0 = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
    return Math.ceil(((t.getTime() - y0.getTime()) / 864e5 + 1) / 7);
}
